import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface AuthCredentials {
  username: string;
  password: string;
}

interface AuthState {
  isAuthenticated: boolean;
  credentials: AuthCredentials | null;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => void;
  logout: () => void;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'itp_auth_credentials';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    credentials: null,
    isLoading: true,
    error: null,
  });

  // Load credentials from localStorage on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem(AUTH_STORAGE_KEY);
    if (savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        setAuthState({
          isAuthenticated: true,
          credentials,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Failed to parse saved credentials:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
    } else {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = useCallback((username: string, password: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    // For now, we'll just save the credentials
    // In a real app, you'd validate against an API
    const credentials = { username, password };

    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(credentials));
      setAuthState({
        isAuthenticated: true,
        credentials,
        isLoading: false,
        error: null,
      });
    } catch {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Nie udało się zapisać danych logowania',
      }));
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthState({
      isAuthenticated: false,
      credentials: null,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setAuthState((prev) => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


